/**
 * 需求来源：docs/新增CJ招聘落地页需求/prompt.md、docs/新增CJ招聘落地页需求/CJ-333291
 * 注意事项：
 * 1. /careers 为纯展示型 SSR 招聘落地页，按目标稿 1:1 还原结构与视觉。
 * 2. 文案统一走 constants.js 的 i18next.t，便于翻译上报；不接入后端接口。
 * 3. 交互（滚动揭示、导航高亮、移动菜单、复制邮箱、浮动按钮）仅在浏览器端生效。
 */
import React, { useState, useCallback, useContext, createContext, useMemo, useEffect } from 'react';
import withSSR from '../../hoc/withSSR';
import fetchData from './fetch';
import style from './index.less';
import { getConstants, LINKS } from './constants';

const ConstantsContext = createContext({});

function joinClassNames() {
  return Array.prototype.slice.call(arguments).filter(Boolean).join(' ');
}

function scrollToSection(id) {
  if (typeof document === 'undefined') return;
  var el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

/** 内联图标，等价于目标稿的 material-symbols，避免依赖外部字体 */
function Icon({ name }) {
  var icons = {
    explore: <path d="M12 2a10 10 0 1 0 0 20a10 10 0 0 0 0-20Zm3.5 6.5-2 5-5 2 2-5 5-2Z" />,
    work: <path d="M9 4h6a2 2 0 0 1 2 2v1h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3V6a2 2 0 0 1 2-2Zm0 3h6V6H9v1Z" />,
    send: <path d="M3 11l18-8-8 18-2-7-8-3Z" />,
    public: <path d="M12 2a10 10 0 1 0 0 20a10 10 0 0 0 0-20Zm0 2c.9 0 2.5 2.6 2.9 6H9.1C9.5 6.6 11.1 4 12 4Zm-7.7 7H8c.1 1 .3 2 .5 3H4.9A8 8 0 0 1 4.3 11Zm0-2A8 8 0 0 1 8.5 5.4C8.2 6.5 8 7.7 8 9H4.3ZM12 20c-.9 0-2.5-2.6-2.9-6h5.8c-.4 3.4-2 6-2.9 6Zm7.1-6h-3.6c.2-1 .4-2 .5-3h3.7a8 8 0 0 1-.6 3ZM16 9c0-1.3-.2-2.5-.5-3.6A8 8 0 0 1 19.7 9H16Z" />,
    apartment: <path d="M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3h3a2 2 0 0 1 2 2v11h-7v-4h-3v4H5Zm3-12h2V7H8v2Zm0 4h2v-2H8v2Z" />,
    domain: <path d="M3 21V7l6-3v3l6-3v6h6v11H3Zm4-2h2v-2H7v2Zm0-4h2v-2H7v2Zm4 4h2v-2h-2v2Zm0-4h2v-2h-2v2Z" />,
    flight: <path d="M21 16v-2l-8-5V3.5A1.5 1.5 0 0 0 11.5 2A1.5 1.5 0 0 0 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1l3.5 1v-1.5L13 19v-5.5L21 16Z" />,
    rocket: <path d="M12 2c3.3 2.4 5 5.9 5 9.5v3.2l1.7 1.7c.2.2.3.4.3.7V20a1 1 0 0 1-1.3.9L14 19.7a2 2 0 0 1-4 0l-3.7 1.2A1 1 0 0 1 5 20v-2.9c0-.3.1-.5.3-.7L7 14.7v-3.2C7 7.9 8.7 4.4 12 2Zm0 6a2 2 0 1 0 0 4a2 2 0 0 0 0-4Z" />,
    paid: <path d="M12 2a10 10 0 1 0 0 20a10 10 0 0 0 0-20Zm1 14.5V18h-2v-1.4c-1.4-.3-2.5-1.2-2.6-2.6h1.8c.1.7.7 1.2 1.8 1.2c1 0 1.6-.4 1.6-1c0-.6-.4-.9-1.8-1.2c-1.8-.4-3.1-1-3.1-2.6c0-1.3 1-2.1 2.3-2.4V6h2v1.4c1.3.3 2.2 1.1 2.3 2.4h-1.8c-.1-.6-.5-1-1.5-1c-.9 0-1.5.4-1.5 1c0 .5.5.8 1.9 1.1c1.8.4 3 1 3 2.7c0 1.3-1 2.2-2.5 2.4Z" />,
    diversity: <path d="M12 3a3 3 0 1 1 0 6a3 3 0 0 1 0-6ZM5 11a2.5 2.5 0 1 1 0-5a2.5 2.5 0 0 1 0 5Zm14 0a2.5 2.5 0 1 1 0-5a2.5 2.5 0 0 1 0 5Zm-7 1c2.7 0 5 1.5 5 3.5V19H7v-3.5C7 13.5 9.3 12 12 12Zm-7 1.2c-1.7.3-3 1.3-3 2.8V19h3v-2.5c0-1.2.4-2.3 1-3.1c-.3-.1-.7-.2-1-.2Zm14 0c-.3 0-.7.1-1 .2c.6.8 1 1.9 1 3.1V19h3v-3c0-1.5-1.3-2.5-3-2.8Z" />,
    celebration: <path d="M4 22a1 1 0 0 1-1-1.2L6 10l8 8l-9.7 3.9A1 1 0 0 1 4 22Zm4.2-11.6l5.4 5.4l3.7-1.5a1 1 0 0 0 .3-1.6l-6.3-6.3a1 1 0 0 0-1.6.3l-1.5 3.7ZM15 3.5a1 1 0 0 1 2 0c0 1.4-.6 2.2-1.4 2.7c.3-.9.3-1.7-.6-2.7Zm3.5.9a1 1 0 0 1 1.4 1.4c-1 1-1.9 1.1-2.8.9c.8-.5 1.5-1.1 1.4-2.3ZM20 9a1 1 0 0 1 0 2c-1.4-.2-2-.9-2.5-1.7c1 .3 1.8.1 2.5-.3Z" />,
    school: <path d="M12 3L1 9l11 6l9-4.9V17h2V9L12 3Zm0 13l-6-3.3v3.5C6 18 9 19.5 12 19.5s6-1.5 6-3.3v-3.5L12 16Z" />,
    forum: <path d="M3 4h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H7l-4 3V5a1 1 0 0 1 0-1Zm17 4h1a1 1 0 0 1 1 1v11l-4-3h-9v-2h10V8Z" />,
    groups: <path d="M8 11a3 3 0 1 0 0-6a3 3 0 0 0 0 6Zm8 0a3 3 0 1 0 0-6a3 3 0 0 0 0 6Zm-8 2c-2.7 0-6 1.3-6 4v2h12v-2c0-2.7-3.3-4-6-4Zm8 0c-.6 0-1.3.1-2 .2c1.2.9 2 2.1 2 3.8v2h6v-2c0-2.7-3.3-4-6-4Z" />,
    verified: <path d="m12 2l2.4 1.8l3-.3l.9 2.9l2.6 1.5l-1 2.9l1 2.9l-2.6 1.5l-.9 2.9l-3-.3L12 22l-2.4-1.8l-3 .3l-.9-2.9L3.1 16l1-2.9l-1-2.9l2.6-1.5l.9-2.9l3 .3L12 2Zm-1 13l5-5l-1.4-1.4L11 12.2L9.4 10.6L8 12l3 3Z" />,
    chat: <path d="M3 4h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H7l-4 4V5a1 1 0 0 1 0-1Z" />,
    mail: <path d="M3 5h18a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Zm9 7l8-5H4l8 5Z" />,
    copy: <path d="M8 2h9a2 2 0 0 1 2 2v12h-2V4H8V2Zm-3 4h9a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" />,
    check: <path d="M9 16.2L4.8 12l-1.4 1.4L9 19L21 7l-1.4-1.4L9 16.2Z" />,
    fire: <path d="M12 23c4 0 7-2.9 7-6.8c0-2.5-1-4.7-2.6-6.7c-.4 1-1.1 1.8-2.1 2.2c.6-2.4-.3-5.4-2.6-7.7c-.6-.6-1.4-1.1-2.3-1.5c.5 2.6-.4 4.4-1.7 6c-1.5 1.8-3.4 3.4-3.4 6.5C4.3 19.6 7.6 23 12 23Zm.2-3.2c-1.6 0-2.9-1.1-2.9-2.7c0-1.4.9-2.2 1.6-3c.4 1 1.4 1.6 2.4 1.6c1 0 1.6-.6 2-1.4c1 1 1.7 2.1 1.7 3.2c0 1.4-1.3 2.3-2.9 2.3Z" />,
    location: <path d="M12 2a7 7 0 0 1 7 7c0 5-7 13-7 13S5 14 5 9a7 7 0 0 1 7-7Zm0 9.5A2.5 2.5 0 1 0 12 6.5a2.5 2.5 0 0 0 0 5Z" />,
    arrow: <path d="M5 12h12l-5-5l1.4-1.4L21.8 12l-8.4 6.4L12 17l5-5H5v0Z" />,
    down: <path d="M12 16l-6-6l1.4-1.4L12 13.2l4.6-4.6L18 10l-6 6Z" />,
    play: <path d="M12 2a10 10 0 1 0 0 20a10 10 0 0 0 0-20Zm-2 6l7 4l-7 4V8Z" />,
    beach: <path d="M3 21h18v2H3v-2Zm9.4-18.3c3.6 1 6 3.9 6.6 7.2L4.7 6.2c2.1-2.5 5.1-3.6 7.7-3.5ZM2.8 8.1l16.5 4.4l-.5 1.9L13 12.9L11 21H9l1.8-8.6l-8.5-2.3l.5-2Z" />,
    hub: <path d="M12 2a3 3 0 0 1 2.8 4.1l2.6 2.6a3 3 0 1 1-1.4 1.4L13.4 7.5a3 3 0 0 1-2.8 0l-2.6 2.6a3 3 0 1 1-1.4-1.4l2.6-2.6A3 3 0 0 1 12 2Zm0 13a3 3 0 1 1 0 6a3 3 0 0 1 0-6Z" />,
    travel: <path d="M12 2a10 10 0 1 0 0 20a10 10 0 0 0 0-20Zm5.9 6h-2.8a15 15 0 0 0-1.2-3.2A8 8 0 0 1 17.9 8ZM12 4c.8 1.1 1.4 2.5 1.8 4h-3.6c.4-1.5 1-2.9 1.8-4ZM4.3 14A8 8 0 0 1 4 12c0-.7.1-1.4.3-2h3.2c-.1.7-.2 1.3-.2 2s.1 1.3.2 2H4.3Zm.8 2h2.8c.3 1.2.7 2.3 1.2 3.2A8 8 0 0 1 5.1 16Zm2.8-8H5.1a8 8 0 0 1 4-3.2A15 15 0 0 0 7.9 8ZM12 20c-.8-1.1-1.4-2.5-1.8-4h3.6c-.4 1.5-1 2.9-1.8 4Zm2.2-6H9.8c-.1-.7-.2-1.3-.2-2s.1-1.3.2-2h4.4c.1.7.2 1.3.2 2s-.1 1.3-.2 2Zm.9 5.2c.5-.9.9-2 1.2-3.2h2.8a8 8 0 0 1-4 3.2Zm1.6-5.2c.1-.7.2-1.3.2-2s-.1-1.3-.2-2h3.2c.2.6.3 1.3.3 2s-.1 1.4-.3 2h-3.2Z" />,
    landscape: <path d="M3 20l5.5-7l3.5 4.5l2.5-3.2L21 20H3Zm14-9a2 2 0 1 1 0-4a2 2 0 0 1 0 4Z" />,
    warehouse: <path d="M3 21V8l9-4l9 4v13h-5v-7H8v7H3Zm7 0v-5h4v5h-4Z" />,
    fullscreen: <path d="M4 9V4h5v2H6v3H4Zm0 6h2v3h3v2H4v-5Zm16-6h-2V6h-3V4h5v5Zm0 6v5h-5v-2h3v-3h2Z" />,
    more: <path d="M6 10a2 2 0 1 1 0 4a2 2 0 0 1 0-4Zm6 0a2 2 0 1 1 0 4a2 2 0 0 1 0-4Zm6 0a2 2 0 1 1 0 4a2 2 0 0 1 0-4Z" />
  };
  return (
    <svg className={style.icon} viewBox="0 0 24 24" aria-hidden="true">
      {icons[name] || null}
    </svg>
  );
}

/**
 * 滚动揭示：用 data-revealed 属性（React 不参与协调）记录揭示态，
 * 避免组件因 state 重渲染时 className 被协调回原值而丢失揭示 class（vthumb 点击消失的根因）。
 */
function useReveal() {
  useEffect(function() {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;
    var els = document.querySelectorAll('[data-reveal]');
    var io = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.setAttribute('data-revealed', '1');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach(function(el) { io.observe(el); });
    return function() { io.disconnect(); };
  }, []);
}

function HeroSection() {
  const { HERO } = useContext(ConstantsContext);
  return (
    <header className={style.hero} id="top">
      <div className={style.heroBg}>
        <img src={HERO.image} alt={HERO.imageAlt} />
      </div>
      <div className={style.container}>
        <div className={style.heroInner} data-reveal="">
          <div className={style.hiringBadgePlaceholder} aria-hidden="true" />
          <h1>{HERO.title}<br /><span>{HERO.titleHighlight}</span></h1>
          <div className={style.heroKicker}>{HERO.kicker}</div>
          <p>{HERO.desc}</p>
          <div className={style.heroCta}>
            <a className={joinClassNames(style.btnHero, style.btnHeroPrimary)} data-gtag-element="careers_hero_jobs" onClick={function() { scrollToSection('jobs'); }}>
              <Icon name="work" />{HERO.primaryCta}
            </a>
            <a className={joinClassNames(style.btnHero, style.btnHeroGhost)} data-gtag-element="careers_hero_apply" onClick={function() { scrollToSection('contact'); }}>
              <Icon name="send" />{HERO.secondaryCta}
            </a>
          </div>
          <div className={style.heroTags}>
            {HERO.tags.map(function(tag) {
              return <span key={tag} className={style.pill}><Icon name="public" />{tag}</span>;
            })}
            <div className={style.hiringBadge}><span className={style.dot} />{HERO.badge}</div>
          </div>
        </div>
      </div>
      <a className={style.scrollHint} data-gtag-element="careers_hero_scroll" onClick={function() { scrollToSection('markets'); }}><Icon name="down" /></a>
    </header>
  );
}

/** 将 label 中的 highlight 子串渲染为高亮，其余原样返回 */
function renderLabelWithHighlight(label, highlight) {
  if (!highlight) return label;
  var idx = label.indexOf(highlight);
  if (idx < 0) return label;
  return [
    label.slice(0, idx),
    <b key="hl" className={style.mLabelHl}>{highlight}</b>,
    label.slice(idx + highlight.length)
  ];
}

function MarketsSection() {
  const { MARKETS } = useContext(ConstantsContext);
  const list = MARKETS.countries.concat(MARKETS.countries);
  return (
    <section className={style.markets} id="markets">
      <div className={style.mLabel}>{renderLabelWithHighlight(MARKETS.label, MARKETS.labelHighlight)}</div>
      <div className={style.marquee}>
        <div className={style.marqueeTrack}>
          {list.map(function(c, idx) {
            return (
              <span key={c.code + '-' + idx} className={style.country}>
                <img src={'https://flagcdn.com/w80/' + c.code + '.png'} alt={c.name} loading="lazy" />
                {c.name}
              </span>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, title, subtitle, center }) {
  return (
    <div className={joinClassNames(style.sectionHead, center ? style.sectionHeadCenter : '')} data-reveal="">
      <div className={style.chapter}>{eyebrow}</div>
      <h2>{title}</h2>
      {subtitle ? <div className={style.sub}>{subtitle}</div> : null}
    </div>
  );
}

function GlobalSection() {
  const { GLOBAL } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.block, style.global)} id="global">
      <div className={style.container}>
        <SectionHead eyebrow={GLOBAL.eyebrow} title={GLOBAL.title} subtitle={GLOBAL.subtitle} center />
        <div className={style.hubGrid}>
          {GLOBAL.hubs.map(function(hub) {
            return (
              <div key={hub.title} className={style.hub} data-reveal="">
                <div className={style.hubIc}><Icon name={hub.icon} /></div>
                <h3>{hub.title}</h3>
                <p>{hub.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function WhySection() {
  const { WHY } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.block, style.why)} id="why">
      <div className={style.container}>
        <SectionHead eyebrow={WHY.eyebrow} title={WHY.title} subtitle={WHY.subtitle} center />
        <div className={style.whyGrid}>
          {WHY.perks.map(function(perk) {
            return (
              <div key={perk.title} className={style.perk} data-reveal="">
                <div className={style.perkIc}><Icon name={perk.icon} /></div>
                <h3>{perk.title}</h3>
                <p>{perk.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Step Inside Our World：缩略图切换主视频，遮罩与按钮常驻不消失（仅切换，非沉浸播放） */
function LifeSection() {
  const { LIFE } = useContext(ConstantsContext);
  const [active, setActive] = useState(0);
  const current = LIFE.thumbs[active] || LIFE;

  const selectThumb = useCallback(function(idx) {
    setActive(idx);
  }, []);

  // 有真实视频源时点击就地播放，但保留遮罩与按钮（按需求不消失）
  const play = useCallback(function() {
    if (typeof document === 'undefined') return;
    var v = document.getElementById('careersMainVideo');
    if (v && v.play && current.video) v.play();
  }, [current.video]);

  // 全屏播放：调用原生 requestFullscreen（含 webkit 前缀），并尝试播放
  const goFullscreen = useCallback(function() {
    if (typeof document === 'undefined') return;
    var v = document.getElementById('careersMainVideo');
    if (!v) return;
    var req = v.requestFullscreen || v.webkitRequestFullscreen || v.webkitEnterFullscreen || v.msRequestFullscreen;
    if (req) req.call(v);
    if (v.play && current.video) v.play();
  }, [current.video]);

  // 封面用视频首帧：poster 取 OSS video/snapshot 首帧图（见 constants.videoPoster）。
  // src 仍追加 #t=0.1 作为无 poster 时的兜底（浏览器定位首帧画面）。
  const videoSrc = current.video ? current.video + '#t=0.1' : undefined;

  return (
    <section className={joinClassNames(style.block, style.life)} id="life">
      <div className={style.container}>
        <SectionHead eyebrow={LIFE.eyebrow} title={LIFE.title} subtitle={LIFE.subtitle} center />
        <div className={style.videoWrap} data-reveal="">
          <video id="careersMainVideo" poster={current.poster} preload="metadata" playsInline src={videoSrc} />
          <button className={style.fsBtn} title={LIFE.fullscreenCta} aria-label={LIFE.fullscreenCta} data-gtag-element="careers_video_fullscreen" onClick={goFullscreen}>
            <Icon name="fullscreen" />
          </button>
          <div className={style.videoOverlay}>
            <span className={style.videoBadge}>{LIFE.badge}</span>
            <h3>{current.title || LIFE.featureTitle}</h3>
            <p>{LIFE.featureDesc}</p>
            <button className={style.playBtn} data-gtag-element="careers_play_video" onClick={play}>
              <Icon name="play" />{LIFE.playCta}
            </button>
          </div>
        </div>
        <div className={style.vthumbs}>
          {LIFE.thumbs.map(function(t, idx) {
            return (
              <div
                key={t.title}
                className={joinClassNames(style.vthumb, idx === active ? style.vthumbActive : '')}
                data-reveal=""
                data-gtag-element="careers_video_thumb"
                onClick={function() { selectThumb(idx); }}
              >
                <div className={style.vtIc}><Icon name={t.icon} /></div>
                <div><h4>{t.title}</h4><span>{t.sub}</span></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Life Beyond the Desk：7 张图瀑布网格，wide/tall 跨格 */
function GallerySection() {
  const { GALLERY } = useContext(ConstantsContext);
  const sizeMap = { wide: style.gcardWide, wideTall: joinClassNames(style.gcardWide, style.gcardTall) };
  return (
    <section className={joinClassNames(style.block, style.work)}>
      <div className={style.container}>
        <SectionHead eyebrow={GALLERY.eyebrow} title={GALLERY.title} subtitle={GALLERY.subtitle} center />
        <div className={style.gallery}>
          {GALLERY.items.map(function(item) {
            return (
              <div key={item.cap} className={joinClassNames(style.gcard, sizeMap[item.size] || '')} data-reveal="">
                <img src={item.image} alt={item.cap} loading="lazy" />
                <div className={style.cap}>{item.cap}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Work Locations · Overseas Postings：海外派驻地标签云，置于 Jobs 与 Gallery 之间 */
function WorksiteSection() {
  const { WORKSITE } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.block, style.worksiteBlock)}>
      <div className={style.container}>
        <div className={style.worksite} data-reveal="">
          <div className={style.worksiteHead}>
            <span className={style.worksiteIc}><Icon name="location" /></span>
            <div>
              <h3>{WORKSITE.title}</h3>
              <p>{WORKSITE.desc}</p>
            </div>
          </div>
          <div className={style.worksiteList}>
            {WORKSITE.locations.map(function(loc) {
              return <span key={loc}>{loc}</span>;
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function JobsSection() {
  const { JOBS } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.block, style.jobs)} id="jobs">
      <div className={style.container}>
        <SectionHead eyebrow={JOBS.eyebrow} title={JOBS.title} subtitle={JOBS.subtitle} center />
        {JOBS.items.map(function(job, index) {
          var reverse = index % 2 === 0;
          return (
            <div key={job.title} className={joinClassNames(style.job, reverse ? style.jobReverse : '')} data-reveal="">
              <div className={style.jobMedia}>
                <span className={style.hot}><Icon name="fire" />{JOBS.hotLabel}</span>
                <img src={job.image} alt={job.title} loading="lazy" />
              </div>
              <div className={style.jobBody}>
                <span className={style.locTag}><Icon name="location" />{JOBS.locationLabel}</span>
                <h3>{job.title}{job.subtitle ? <small>{job.subtitle}</small> : null}</h3>
                <div className={style.jobCols}>
                  <div>
                    <h4><Icon name="verified" />{JOBS.highlightsLabel}</h4>
                    <ul className={style.hl}>
                      {job.highlights.map(function(item) {
                        return <li key={item}><Icon name="check" />{item}</li>;
                      })}
                    </ul>
                  </div>
                  <div>
                    <h4><Icon name="check" />{JOBS.requirementsLabel}</h4>
                    <ul className={style.rq}>
                      {job.requirements.map(function(item) {
                        return <li key={item}><Icon name="arrow" />{item}</li>;
                      })}
                    </ul>
                  </div>
                </div>
                <a className={style.btnLine} data-gtag-element="careers_job_apply" onClick={function() { scrollToSection('contact'); }}>
                  {JOBS.applyCta} <Icon name="arrow" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProcessSection() {
  const { PROCESS } = useContext(ConstantsContext);
  return (
    <section className={joinClassNames(style.block, style.process)}>
      <div className={style.container}>
        <SectionHead eyebrow={PROCESS.eyebrow} title={PROCESS.title} subtitle={PROCESS.subtitle} center />
        <div className={style.steps}>
          {PROCESS.steps.map(function(step) {
            return (
              <div key={step.no} className={style.step} data-reveal="">
                <div className={style.stepNo}>{step.no}</div>
                <div className={style.stepIc}><Icon name={step.icon} /></div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const { CONTACT } = useContext(ConstantsContext);
  const [copied, setCopied] = useState(false);

  const copyMail = useCallback(function() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    navigator.clipboard.writeText(CONTACT.email).then(function() {
      setCopied(true);
      setTimeout(function() { setCopied(false); }, 1500);
    });
  }, [CONTACT.email]);

  return (
    <section className={joinClassNames(style.block, style.contact)} id="contact">
      <div className={style.container}>
        <div className={style.contactCard} data-reveal="">
          <h2>{CONTACT.title}</h2>
          <p>{CONTACT.subtitle}</p>
          <div className={style.contactStack}>
            <div className={style.mailRow}>
              <div className={style.mailLeft}>
                <span className={style.mailIc}><Icon name="mail" /></span>
                <span className={style.mailText}>{CONTACT.email}</span>
              </div>
              <button className={style.copyBtn} title={CONTACT.copyLabel} data-gtag-element="careers_copy_mail" onClick={copyMail}>
                <Icon name={copied ? 'check' : 'copy'} />
              </button>
            </div>
            <div className={style.divider}>{CONTACT.divider}</div>
            <a className={style.btnWa} href={LINKS.whatsapp} target="_blank" rel="noopener noreferrer" data-gtag-element="careers_whatsapp">
              <Icon name="chat" />{CONTACT.whatsappCta}
            </a>
            <div className={style.qrBox}>
              <div className={style.qrImg}>
                {CONTACT.qrImage
                  ? <img src={CONTACT.qrImage} alt={CONTACT.qrLabel} loading="lazy" />
                  : <Icon name="chat" />}
              </div>
              <span className={style.qrLabel}>{CONTACT.qrLabel}</span>
            </div>
            <div className={style.resumeNote}>
              <span className={style.resumeNoteIc}><Icon name="send" /></span>
              <div>{CONTACT.resumeNote}<b>{CONTACT.resumeNoteFields}</b></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FloatApply() {
  const { FLOAT_CTA } = useContext(ConstantsContext);
  const [show, setShow] = useState(false);
  useEffect(function() {
    function onScroll() { setShow(window.scrollY > 700); }
    window.addEventListener('scroll', onScroll);
    return function() { window.removeEventListener('scroll', onScroll); };
  }, []);
  return (
    <a
      className={joinClassNames(style.floatApply, show ? style.floatApplyShow : '')}
      data-gtag-element="careers_float_apply"
      onClick={function() { scrollToSection('contact'); }}
    >
      <Icon name="send" />{FLOAT_CTA}
    </a>
  );
}

/**
 * 需求来源：CJ 招聘落地页目标稿。
 * 注意事项：仅消费 constants 展示数据，所有跳转/复制交互均在浏览器端按需触发。
 */
function CareersPage(props) {
  // fetch.js 注入：cdnHost(=CDN_HOST/OSS_HOST，按环境的 OSS 裸域名，视频用)、publicImg(=PUBLIC_IMG，图片基址)
  var pageData = (props && props.pageData) || {};
  var cdnHost = pageData.cdnHost || '';
  var publicImg = pageData.publicImg || '';
  const constants = useMemo(function() { return getConstants(cdnHost, publicImg); }, [cdnHost, publicImg]);
  useReveal();

  return (
    <ConstantsContext.Provider value={constants}>
      <div className={style.careersPage} data-gtag-module="careers">
        <HeroSection />
        <MarketsSection />
        <GlobalSection />
        <WhySection />
        <LifeSection />
        <JobsSection />
        <WorksiteSection />
        <GallerySection />
        <ProcessSection />
        <ContactSection />
        <FloatApply />
      </div>
    </ConstantsContext.Provider>
  );
}

CareersPage.fetch = fetchData;

export default withSSR(CareersPage);
